import { useUSDCMetadata } from '@components/Web3/UserOpTxModal/usdc/qr-fund/useUSDCAddress'
import { useQuery } from '@tanstack/react-query'
import { UserOps } from '@towns/userops'
import { useBaseWrites } from 'use-towns-client'
import { Address, erc20Abi } from 'viem'

export const allowanceQueryKey = (
    walletAddress: Address | undefined,
    spenderAddress: Address | undefined,
    usdcAddress: Address | undefined,
) =>
    [
        'usdcAllowance',
        walletAddress?.toLowerCase(),
        spenderAddress?.toLowerCase(),
        usdcAddress?.toLowerCase(),
    ] as const

export async function getUSDCAllowance(params: {
    userOpsInstance: UserOps | undefined
    walletAddress: Address | undefined
    spenderAddress: Address | undefined
    usdcAddress: Address | undefined
}): Promise<bigint | undefined> {
    const { userOpsInstance, walletAddress, spenderAddress, usdcAddress } = params
    const publicClient = (await userOpsInstance?.smartAccountSession.smartAccount)?.publicRpcClient
    if (!publicClient) {
        throw new Error('Public client not found')
    }
    if (!walletAddress || !spenderAddress || !usdcAddress) {
        return undefined
    }

    const allowance = await publicClient.readContract({
        address: usdcAddress,
        abi: erc20Abi,
        functionName: 'allowance',
        args: [walletAddress, spenderAddress],
    })

    return allowance
}

export function useUSDCAllowance({
    walletAddress,
    spenderAddress,
    enabled = true,
    refetchInterval = 6_000,
}: {
    walletAddress?: Address
    spenderAddress?: Address
    enabled?: boolean
    refetchInterval?: number
}) {
    const { userOpsInstance } = useBaseWrites()
    const { address: usdcAddress } = useUSDCMetadata()
    return useQuery({
        queryKey: allowanceQueryKey(walletAddress, spenderAddress, usdcAddress),
        queryFn: async () => {
            return getUSDCAllowance({ userOpsInstance, walletAddress, spenderAddress, usdcAddress })
        },
        enabled: enabled && !!walletAddress && !!spenderAddress && !!usdcAddress,
        refetchInterval,
        staleTime: 60_000,
        gcTime: 60_000 * 5,
    })
}
